import {
  EGooglePlayPublishType,
  ICommonSettings,
  IGooglePlayApiSetting,
  IImageLibraryDto
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IGooglePlayConnectApiSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IGooglePlayApiSetting;
  publishType?: EGooglePlayPublishType;
  allImage?: IImageLibraryDto[];
  imageSearchText: string;
  jksFileName?: string;
  subscriptionServiceAccountJson?: string;
}

export interface IGooglePlayConnectApiSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingGooglePlayConnectApiId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class GooglePlayConnectApiSettings extends React.PureComponent<
  IGooglePlayConnectApiSettingsProps,
  IGooglePlayConnectApiSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IGooglePlayConnectApiSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingConfig: undefined,
      publishType: undefined,
      imageSearchText: ""
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IGooglePlayConnectApiSettingsProps & IWithSDProps) {
    if (prevProps.editingGooglePlayConnectApiId !== this.props.editingGooglePlayConnectApiId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingGooglePlayConnectApiId) {
      const setting = this.props.commonSettings.googlePlayApiSettings?.find(
        (x) => x.id === this.props.editingGooglePlayConnectApiId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState({ editingConfig: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          let jksFileName: string | undefined;
          let subscriptionServiceAccountJson: string | undefined;

          this.formRef.current?.resetFields();

          fieldsValue.name = setting.name;
          fieldsValue.publishType = setting.publishType;
          subscriptionServiceAccountJson = setting.subscriptionServiceAccountJson;

          switch (setting.publishType) {
            case EGooglePlayPublishType.Simulator:
              fieldsValue.appetizeApiToken = setting.appetizeApiToken;
              break;
            case EGooglePlayPublishType.AAB:
              fieldsValue.signingStorePassword = setting.signingStorePassword;
              fieldsValue.signingKeyAlias = setting.signingKeyAlias;
              fieldsValue.signingKeyPassword = setting.signingKeyPassword;
              fieldsValue.sha256CertificateFingerprint = setting.sha256CertificateFingerprint;
              jksFileName = setting.jksFileName;
              break;
            case EGooglePlayPublishType.Apk:
              fieldsValue.sha256CertificateFingerprint = setting.sha256CertificateFingerprint;
              break;
          }
          this.setState({ publishType: setting.publishType, jksFileName }, () => {
            this.formRef.current?.setFieldsValue(fieldsValue);
          });
          this.setState(
            {
              publishType: setting.publishType,
              subscriptionServiceAccountJson
            },
            () => {
              this.formRef.current?.setFieldsValue(fieldsValue);
            }
          );
        } else {
          this.formRef.current?.resetFields();
        }
      });
    } else {
      this.setState({ publishType: undefined, jksFileName: undefined }, () => {
        this.formRef.current?.resetFields();
      });
      this.setState({ publishType: undefined, subscriptionServiceAccountJson: undefined }, () => {
        this.formRef.current?.resetFields();
      });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const setting = this.props.commonSettings?.googlePlayApiSettings;
    if (!setting) return;

    const isSettingAlreadyExist = setting.some(
      (x) =>
        x.name === value && x.id !== this.props.editingGooglePlayConnectApiId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Google Play Setting name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IGooglePlayApiSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      publishType: data.publishType,
      appetizeApiToken: data.appetizeApiToken?.trim(),
      jksFileName: this.state.jksFileName,
      subscriptionServiceAccountJson: this.state.subscriptionServiceAccountJson,
      signingStorePassword: data.signingStorePassword?.trim(),
      signingKeyAlias: data.signingKeyAlias?.trim(),
      signingKeyPassword: data.signingKeyPassword?.trim(),
      sha256CertificateFingerprint: data.sha256CertificateFingerprint?.trim()
    };
    if (this.props.editingGooglePlayConnectApiId) {
      obj.id = this.props.editingGooglePlayConnectApiId;

      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googlePlayApiSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googlePlayApiSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingGooglePlayConnectApiId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingGooglePlayConnectApiId || "",
      configKey: "googlePlayApiSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Google Play setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    let contentKeyPrefix = "googleplayconnectapisettingspopup_googleplayconnectapisettings";
    switch (this.state.publishType) {
      case EGooglePlayPublishType.Simulator:
        contentKeyPrefix += "appetize";
        break;
      case EGooglePlayPublishType.Apk:
        contentKeyPrefix += "apk";
        break;
      case EGooglePlayPublishType.AAB:
        contentKeyPrefix += "aab";
        break;
    }

    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Google Play connect API setting"
        secondHeader="Google Play connect API setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingGooglePlayConnectApiId ? this.deleteSetting : undefined}
            deleteTestId="googlePlaySettingModalDeleteButton"
            submitTestId={`googlePlaySettingModal${
              this.props.editingGooglePlayConnectApiId ? "Update" : "Create"
            }Button`}
            submitLabel={this.props.editingGooglePlayConnectApiId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGooglePlayForm"
          />
        }
      >
        <Form
          id="configGooglePlayForm"
          name="googlePlayStoreSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription(`${contentKeyPrefix}_description`)}>
            <div>
              <ConfigField label="Name" required tooltipContentKey={`${contentKeyPrefix}_name`}>
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a Google Play Setting Name."
                    },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                      message: "Google Play Setting Name is invalid"
                    },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="googlePlaySettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>

              <ConfigField label="Publish Type" required tooltipContentKey={`${contentKeyPrefix}_publishtype`}>
                <Form.Item
                  name="publishType"
                  rules={[
                    {
                      required: true,
                      message: "Please select a Publish Type."
                    }
                  ]}
                >
                  <KuikaSelectbox
                    size="md"
                    data-testid="googlePlaySettingPublishDDropdown"
                    defaultValue={this.state.publishType}
                    value={this.state.publishType}
                    onChange={(e) => this.setState({ publishType: e })}
                    options={[
                      {
                        value: EGooglePlayPublishType.Simulator,
                        label: "Appetize",
                        testId: "appetizeOption",
                        searchLabel: "Appetize"
                      },
                      {
                        value: EGooglePlayPublishType.Apk,
                        label: "Apk",
                        testId: "apkOption",
                        searchLabel: "Apk"
                      },
                      {
                        value: EGooglePlayPublishType.AAB,
                        label: "AAB",
                        testId: "aabOption",
                        searchLabel: "AAB"
                      }
                    ]}
                  />
                </Form.Item>
              </ConfigField>

              {this.state.publishType && this.state.publishType === EGooglePlayPublishType.Simulator ? (
                <ConfigField
                  label="Appetize Api Token"
                  required
                  tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsappetize_appetizeapitoken"
                >
                  <Form.Item
                    name="appetizeApiToken"
                    rules={[
                      {
                        required: this.state.publishType === EGooglePlayPublishType.Simulator,
                        message: "Please enter a Appetize Api Token."
                      }
                    ]}
                  >
                    <KuikaInput size="md" data-testid="appetizeApiTokenInput" />
                  </Form.Item>
                </ConfigField>
              ) : this.state.publishType && this.state.publishType === EGooglePlayPublishType.AAB ? (
                <>
                  <ConfigField
                    label="Signing Store File"
                    tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsaab_storefile"
                  >
                    <ConfigFilePicker
                      files={this.state.allImage ?? []}
                      acceptedFileTypes=".jks"
                      getImageLibrary={this.getImageLibrary}
                      selectedId={this.state.jksFileName}
                      fileName={this.state.jksFileName}
                      onSelect={(image: IImageLibraryDto): void => this.setState({ jksFileName: image.fileName })}
                      onClear={(): void => this.setState({ jksFileName: undefined })}
                      chooseTestId="signingStoreFileChooseButton"
                    />
                  </ConfigField>

                  <ConfigField
                    label="Signing Store Password"
                    tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsaab_storepassword"
                  >
                    <Form.Item name="signingStorePassword">
                      <KuikaInput
                        size="md"
                        data-testid="signingStorePasswordInput"
                        autoComplete="off"
                        type="password"
                        revealable
                      />
                    </Form.Item>
                  </ConfigField>

                  <ConfigField
                    label="Signing Key Alias"
                    tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsaab_keyalias"
                  >
                    <Form.Item name="signingKeyAlias">
                      <KuikaInput size="md" data-testid="signingKeyAliasInput" />
                    </Form.Item>
                  </ConfigField>

                  <ConfigField
                    label="Signing Key Password"
                    tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsaab_keypassword"
                  >
                    <Form.Item name="signingKeyPassword">
                      <KuikaInput
                        size="md"
                        data-testid="signingKeyPasswordInput"
                        autoComplete="off"
                        type="password"
                        revealable
                      />
                    </Form.Item>
                  </ConfigField>

                  <ConfigField
                    label="SHA-256 certificate fingerprint"
                    tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsaab_sha_256"
                  >
                    <Form.Item name="sha256CertificateFingerprint">
                      <KuikaInput size="md" data-testid="aabSha256CertificateInput" />
                    </Form.Item>
                  </ConfigField>
                </>
              ) : this.state.publishType === EGooglePlayPublishType.Apk ? (
                <>
                  <ConfigField
                    label="SHA-256 certificate fingerprint"
                    tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsaab_sha_256"
                  >
                    <Form.Item name="sha256CertificateFingerprint">
                      <KuikaInput size="md" data-testid="apkSha256CertificateInput" />
                    </Form.Item>
                  </ConfigField>
                </>
              ) : (
                <></>
              )}

              <ConfigField
                label="App Subscription Service Account Json File"
                tooltipContentKey="googleplayconnectapisettingspopup_googleplayconnectapisettingsaab_subscriptionfile"
              >
                <ConfigFilePicker
                  files={this.state.allImage ?? []}
                  acceptedFileTypes=".json"
                  getImageLibrary={this.getImageLibrary}
                  selectedId={this.state.subscriptionServiceAccountJson}
                  fileName={this.state.subscriptionServiceAccountJson}
                  onSelect={(image: IImageLibraryDto): void =>
                    this.setState({ subscriptionServiceAccountJson: image.fileName })
                  }
                  onClear={(): void => this.setState({ subscriptionServiceAccountJson: undefined })}
                  chooseTestId="subscriptionServiceAccountJsonFileChooseButton"
                />
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
